/* eslint-disable indent */
import API from "../../api";
import urls from "../urls";

const { GET_ALL_DELIVERY_AGENTS, ASSIGN_DA_HOTSPOT } = urls;

/**
 * DAApiService - Handles all DA related API operations
 * Following Single Responsibility Principle
 */
class DAApiService {
  /**
   * Fetch all delivery agents with pagination and filters
   * @param {Object} params - Query parameters (dynamically passes all non-empty params to API)
   * @param {string} params.page - Page number (default: "1")
   * @param {string} params.limit - Items per page (default: "10")
   * @param {string} [params.mobile] - Mobile number filter
   * @param {string} [params.name] - Name filter
   * @param {string} [params.vehicleType] - Vehicle type filter
   * @param {string} [params.status] - Status filter
   * @param {string} [params.deliveryAgentId] - Delivery Agent ID filter
   * @param {string} [params.dpId] - Delivery Partner ID filter
   * @param {string} [params.search] - Search term for DA name/mobile
   * @returns {Promise<Object>} API response with DAs list
   */
  async getAllDeliveryAgents(params = {}) {
    try {
      // Start with default pagination parameters
      const queryParams = {
        page: params.page || "1",
        limit: params.limit || "10",
      };

      // Dynamically add all other non-empty parameters
      Object.keys(params).forEach((key) => {
        if (
          key !== "page" &&
          key !== "limit" &&
          params[key] &&
          params[key].toString().trim() !== ""
        ) {
          queryParams[key] = params[key];
        }
      });

      const response = await API.Api(GET_ALL_DELIVERY_AGENTS).getwithParams(
        queryParams
      );

      if (response.status && response.response?.data) {
        const responseData = response.response.data;
        return {
          success: true,
          data: responseData.data || [],
          totalPages: responseData.totalPages || 0,
          totalRecords: responseData.totalRecords || 0,
          currentPage: responseData.pageNumber || 1,
          pageLimit: responseData.pageLimit || 20,
        };
      }

      return {
        success: false,
        message:
          response.response?.data?.message || "Failed to fetch delivery agents",
        data: [],
        totalPages: 0,
        totalRecords: 0,
        currentPage: 1,
      };
    } catch (error) {
      console.error("Error fetching delivery agents:", error);
      return {
        success: false,
        message: error.message || "Network error occurred",
        data: [],
        totalPages: 0,
        totalRecords: 0,
        currentPage: 1,
      };
    }
  }

  /**
   * Assign hotspot to delivery agent
   * @param {Object} data - Assignment data
   * @param {string} data.deliveryAgentId - DA ID to assign hotspot to
   * @param {string} data.hotspotId - Hotspot ID to assign
   * @returns {Promise<Object>} API response
   */
  async updateDAHotspot(data) {
    try {
      const response = await API.Api(ASSIGN_DA_HOTSPOT).post({
        deliveryAgentId: data.deliveryAgentId,
        hotspotId: data.hotspotId,
        dpId: data.dpId,
        partnerType: data.partnerType,
      });

      if (response.status && response.response?.data) {
        const responseData = response.response.data;

        if (responseData.status) {
          return {
            success: true,
            message: responseData.message || "Hotspot assigned successfully",
            data: {
              deliveryAgentId: data.deliveryAgentId,
              hotspotId: data.hotspotId,
              assignedAt: new Date().toISOString(),
              dpId: data.dpId,
              daType: data.daType,
            },
          };
        } else {
          return {
            success: false,
            message: responseData.message || "Failed to assign hotspot",
          };
        }
      }

      return {
        success: false,
        message: response.response?.data?.message || "Failed to assign hotspot",
      };
    } catch (error) {
      console.error("Error assigning DA hotspot:", error);
      return {
        success: false,
        message:
          error.response?.data?.message ||
          error.message ||
          "Network error occurred",
      };
    }
  }

  /**
   * Search delivery agents by name or mobile
   * @param {string} searchTerm - Search term
   * @param {Object} additionalParams - Additional query parameters
   * @returns {Promise<Object>} API response with filtered DAs
   */
  async searchDeliveryAgents(searchTerm, additionalParams = {}) {
    return this.getAllDeliveryAgents({
      search: searchTerm,
      ...additionalParams,
    });
  }
}

// Export singleton instance
export const daApiService = new DAApiService();
export default daApiService;
